import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import useIdleTimeout from "../hooks/useIdleTimeout";
import IdleTimeoutModal from "./IdleTimeoutModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  admin: "Administrador",
  funcionario: "Funcionario",
  contribuyente: "Contribuyente",
  auditor: "Auditor",
};

/**
 * Guardian global de inactividad. Lee la configuración de idle-timeout del backend
 * según el rol y monitorea actividad del usuario. Muestra modal a 60s del cierre
 * y desloguea si no hay extensión.
 */
export default function IdleTimeoutGuard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);

  // Cargar configuración al loguearse (depende de rol)
  useEffect(() => {
    if (!user || !token) {
      setConfig(null);
      return;
    }
    let mounted = true;
    axios.get(`${API}/auth/idle-config`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (mounted) setConfig(r.data);
      })
      .catch(() => setConfig({ idle_timeout_minutes: 15, warning_seconds_before: 60, role: user.role }));
    return () => { mounted = false; };
  }, [user, token]);

  const idleMs = config ? config.idle_timeout_minutes * 60 * 1000 : null;
  const warningMs = config ? config.warning_seconds_before * 1000 : 60_000;

  const handleTimeout = () => {
    toast.error("Tu sesión expiró por inactividad. Por motivos de seguridad debes iniciar sesión nuevamente.", { duration: 6000 });
    if (logout) logout();
    navigate("/login", { replace: true });
  };

  const { showWarning, secondsRemaining, extendSession } = useIdleTimeout({
    idleTimeoutMs: idleMs,
    warningMs,
    onTimeout: handleTimeout,
    enabled: Boolean(user && config && idleMs),
  });

  if (!user || !config) return null;

  return (
    <IdleTimeoutModal
      open={showWarning}
      secondsRemaining={secondsRemaining}
      idleTimeoutMinutes={config.idle_timeout_minutes}
      role={ROLE_LABELS[config.role] || config.role}
      onExtend={() => {
        // Hacer ping al backend para refrescar last_activity_at del lado servidor
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        extendSession();
        toast.success("Sesión extendida correctamente");
      }}
      onLogout={() => {
        if (logout) logout();
        navigate("/login", { replace: true });
      }}
    />
  );
}
